// src/utils/analytics.js
//
// Thin wrappers around the global gtag (loaded in public/index.html) for the
// GA4 enhanced-ecommerce funnel. Each helper is a no-op when gtag isn't
// available — safe to call from anywhere without guards.
//
// Currency is fixed to TZS since the entire site transacts in Tanzanian
// shillings; if a multi-currency mode ever lands, lift this into config.

const CURRENCY = 'TZS';

function send(event, params) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', event, params);
}

function itemFromProduct(product, { size, quantity = 1 } = {}) {
  const item = {
    item_id: String(product.id),
    item_name: product.name,
    item_category: product.category,
    price: product.price,
    quantity,
  };
  if (size != null && size !== '') item.item_variant = String(size);
  return item;
}

function itemFromCartLine(line) {
  const item = {
    item_id: String(line.id),
    item_name: line.name,
    item_category: line.category,
    price: line.price,
    quantity: line.quantity || 1,
  };
  if (line.size != null && line.size !== '') item.item_variant = String(line.size);
  return item;
}

export function trackPageView({ path, title }) {
  send('page_view', {
    page_path: path,
    page_title: title,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

export function trackViewItem(product) {
  if (!product) return;
  send('view_item', {
    currency: CURRENCY,
    value: product.price,
    items: [itemFromProduct(product)],
  });
}

export function trackAddToCart(product, { size, quantity = 1 } = {}) {
  if (!product) return;
  send('add_to_cart', {
    currency: CURRENCY,
    value: product.price * quantity,
    items: [itemFromProduct(product, { size, quantity })],
  });
}

export function trackBeginCheckout(cart) {
  if (!Array.isArray(cart) || cart.length === 0) return;
  const items = cart.map(itemFromCartLine);
  const value = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  send('begin_checkout', { currency: CURRENCY, value, items });
}

export function trackViewItemList(products, { listId, listName } = {}) {
  if (!Array.isArray(products) || products.length === 0) return;
  send('view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items: products.map((p, i) => ({ ...itemFromProduct(p), index: i + 1 })),
  });
}

export function trackSelectItem(product, { listId, listName } = {}) {
  if (!product) return;
  send('select_item', {
    item_list_id: listId,
    item_list_name: listName,
    items: [itemFromProduct(product)],
  });
}

export function trackRemoveFromCart(line) {
  if (!line) return;
  const item = itemFromCartLine(line);
  send('remove_from_cart', {
    currency: CURRENCY,
    value: item.price * item.quantity,
    items: [item],
  });
}

export function trackViewPromotion({ id, name, slot, creative } = {}) {
  if (!id && !name) return;
  send('view_promotion', {
    promotion_id: id,
    promotion_name: name,
    creative_slot: slot,
    creative_name: creative,
  });
}

export function trackSelectPromotion({ id, name, slot, creative } = {}) {
  if (!id && !name) return;
  send('select_promotion', {
    promotion_id: id,
    promotion_name: name,
    creative_slot: slot,
    creative_name: creative,
  });
}

// Exported so Checkout.js can surface the same item shape on the purchase
// event — keeps GA reports consistent across the funnel.
export { itemFromCartLine };
